import { ComponentProps } from "react";
import { Icon, Typography } from "@/components/atoms";
import { NOT_FLYING_LABEL } from "@/helpers/bookingHelpers";
import { BookingFlightStatus } from "@/redux/slices/booking/types";
import { clsx } from "@/utils";

export interface BookingFlightNumberProps extends ComponentProps<typeof Typography> {
  status: BookingFlightStatus;
  number: string;
  delay?: number;
}

type StatusBadgeVariant = "success" | "info" | "warning" | "error" | "flagged" | "neutral";

const statusBadgeColors: Record<StatusBadgeVariant, { badge: string; icon: string }> = {
  success: { badge: "border-success bg-success-light text-success-dark", icon: "text-success" },
  info: { badge: "border-info bg-info-light text-info-dark", icon: "text-info" },
  warning: { badge: "border-warning bg-warning-light text-warning-dark", icon: "text-warning" },
  error: { badge: "border-danger bg-danger-light text-danger-dark", icon: "text-danger" },
  flagged: { badge: "border-status-flagged bg-status-flagged-background text-status-flagged-text", icon: "text-status-flagged" },
  // DSN-689 darkened Neutral's border and plane from Silver Grey to Dark Grey, for the booking sheet and this table alike.
  neutral: { badge: "border-neutral-dark-gray bg-status-neutral-background text-status-neutral-text", icon: "text-neutral-dark-gray" },
};

const flightBadgeVariant: Record<BookingFlightStatus, StatusBadgeVariant> = {
  not_found: "neutral",
  unknown: "neutral",
  scheduled: "success",
  "en-route": "success",
  "on-time": "success",
  landed: "success",
  early: "info",
  delayed: "warning",
  cancelled: "error",
  diverted: "flagged",
  incident: "flagged",
};

const StatusBadge = ({ className, ...props }: ComponentProps<typeof Typography>) => (
  <Typography
    variant="action"
    className={clsx("flex h-6 items-center gap-2 whitespace-nowrap rounded-lg border px-2 leading-5", className)}
    {...props}
  />
);

export const BookingFlightNumber = ({ status, number, delay, className, ...props }: BookingFlightNumberProps) => {
  const activeStatusList = ["scheduled", "en-route"];
  const statusWithDelay =
    typeof delay === "number" && activeStatusList.includes(status) ? (delay > 0 ? "delayed" : delay < 0 ? "early" : "on-time") : status;
  const colors = statusBadgeColors[flightBadgeVariant[statusWithDelay]];

  return (
    <StatusBadge className={clsx("uppercase", colors.badge, className)} {...props}>
      <Icon name="Airplane" size="md" variant="Linear" className={clsx("shrink-0", colors.icon)} />
      {number}
    </StatusBadge>
  );
};

// The DEV-1990 list frame (7331:26874) borders this badge in the neutral text colour, not Status/Neutral.
export const BookingNotFlying = ({ className, ...props }: ComponentProps<typeof Typography>) => (
  <StatusBadge
    className={clsx("border-status-neutral-text bg-status-neutral-background text-status-neutral-text", className)}
    {...props}
  >
    {NOT_FLYING_LABEL}
  </StatusBadge>
);
